import {
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core';

import Snackbar from '@material-ui/core/Snackbar';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import EditIcon from '@material-ui/icons/Edit';
import ErrorIcon from '@material-ui/icons/Error';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import EnhancedTable, { HeadCell } from '../../common/components/EnhancedTable';
import ItemsDrawer from '../../common/components/ItemsDrawer';
import PurchaseOrderEmitDialog from './PurchaseOrderEmitDialog';
import PurchaseOrderEmitHeader from './PurchaseOrderEmitHeader';
import { customtheme } from '../../app/theme/theme';
import PurchaseOrderEmitDrawer from './PurchaseOrderEmitDrawer';
import DialogSearch from '../PurchaseOrderEmit/DialogSearch';
import { mapperToPurchaseOrder } from '../../app/mappers/purchase-order/purchase-order.mapper';
import { handleCheckPrevValue } from '../../common/functions/checkPrevValue';
import { getDraftEmitPurchaseOrderByUser } from '../../app/services/purchase-order/get-emit-purchase-order-module.api';
import { deleteEmitPurchaseOrder } from '../../app/services/purchase-order/delete-emit-purchase-order.api';
import { updateEmitPurchaseOrder } from '../../app/services/purchase-order/update-emit-purchase-order.api';
import { processEmitPurchaseOrder } from '../../app/services/purchase-order/process-emit-purchase-order.api';
import { createEmitPurchaseOrderDraft } from '../../app/services/purchase-order/create-emit-purchase-order.api';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: customtheme.gray[200],
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  topBar: {
    backgroundColor: customtheme.gray[200],
    padding: theme.spacing(2),
    paddingTop: theme.spacing(4),
  },  
  topBarContent: {   
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  topBarButtons: {
    '& > *': {
      marginLeft: theme.spacing(1),
    },
  },
  content: {
    // position: 'relative',
    // flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: '50%',
    backgroundColor: customtheme.white,
  },
  card: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableSection: {
    padding: theme.spacing(4),
    flex: 1,
    display: 'flex',
  },
  tableSectionContainer: {
    padding: 0, 
    flex: 1,
    display: 'flex', 
    flexDirection: 'column',
    maxWidth: '100%',
  },
  appbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    background: customtheme.white,
    color: customtheme.black,
    padding: theme.spacing(2),
    margin: 1,
  },  
  scrollableTabs: {
    overflowX: 'auto',
  }, drawer: {
    width: 400,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: customtheme.gray[200],
  },
  drawerContent: {
    overflowY: 'auto',
    padding: theme.spacing(2), 
    height: '100%',
  },
  drawerFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    background: customtheme.gray[200],
  },
  textField: {
    minWidth: 140,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& .MuiInputBase-input': {
      color: customtheme.black,
    },
    '& .MuiInput-underline:hover:before': {
      borderBottomColor: customtheme.blue[500],
    },
  },
  searchIcon: {
    marginRight: theme.spacing(1),
    color: customtheme.blue[500],
  },
  loadingIcon: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  backgroundColor: {
    background: customtheme.blue[500],
    color: customtheme.white,
    '&:hover': {
      background: customtheme.blue[50],
      color: customtheme.blue[500],
    },
  },
  backBackgroundColor: {
    background: customtheme.gray[200],
    color: customtheme.blue[500],
    '&:hover': {
      background: customtheme.blue[50],
      color: customtheme.blue[500],
    },
  },
  tabsBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabRoot: {
    minWidth: 0,
    paddingLeft: 8,
    paddingRight: 8,
    },
}));

export interface DialogSearchData {
  id: string;
  title: string;
  key: string;
  keySub: string;
  subKeySub?: string;
  isPrevSearch?: boolean;
  prevSearch?: any;
  prevDataSearch: any;
  hasButtonCreate: boolean;
}
interface TabPanelProps {
  value: number;
  index: number;
  children?: React.ReactNode;
}
const TabPanel = ({ value, index, children }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index} style={{ flex: 1 }}>
    {value === index && <Box>{children}</Box>}
  </div>
);

const PurchaseOrderEmit = () => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const handleTabChange = (_: unknown, newValue: number) => setTab(newValue);

  const windowHeight = window.innerHeight;
  const headerHeight = 500; // Altura do cabeçalho em pixels
  const footerHeight = 48; // Altura do rodapé em pixels
  const itemHeight = 80; // Altura média de cada item em pixels
  const availableHeight = windowHeight - headerHeight - footerHeight;
  const itemsPerPage = Math.floor((availableHeight) / itemHeight);

  const [selectedData, setSelectedData] = useState<any>([]);
  const [orderBy, setOrderBy] = useState<string>('numero');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage > 5 ? itemsPerPage : 5);
  const [isDialogSearchOpen, setIsDialogSearchOpen] = useState(false);
  const [dialogDataSearch, setDialogDataSearch] = useState<DialogSearchData | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState({open: false, btnTitle: 'Voltar', btnType: 'back'});
  const [isDrawerItemOpen, setIsDrawerItemOpen] = useState(false)
  const [isDialogFeedbackOpen, setIsDialogFeedbackOpen] = useState({open:false, message: '', title: '', btnType: ''});
  const [isSnackbar, setIsSnackbar] = useState({open:false, message: 'info', title: ''});
  const [isLoading, setIsLoading] = useState(false);
  const [reasonCancel, setReasonCancel] = useState<string>('')
  const fieldsInit = mapperToPurchaseOrder({})
  const [checkPrevCompanyValue, setCheckPrevCompanyValue] = useState({
    exists: false,
    value: ''
  })
  const [checkPrevBranchValue, setCheckPrevBranchValue] = useState({
    exists: false,
    value: ''
  })
  
  const fieldsItems = ['coditem', 'desitem', 'codder', 'desder',
    'qtd', 'valorunitario', 'valortotal', 'datprv', 
    'unidademedida', 'codccu', 'desccu', 'codFinanceira', 'desFinanceira', 'codContabil', 'desContabil'
  ];

  const [selectedFieldsItems, setSelectedFieldsItems] = useState<any[]>([])

  const [fields, setFields] = useState<any[]>(fieldsInit)
  const headCells: HeadCell[] = [
    { id: 'sigemp', numeric: false, disablePadding: true, label: 'Nome Empresa' },
    { id: 'nomfor', numeric: false, disablePadding: true, label: 'Nome Fornecedor' },
    { id: 'valortotal', numeric: false, disablePadding: true, label: 'Valor' },
    { id: 'datemi', numeric: false, disablePadding: true, label: 'Data Emissão' },
    { id: 'actions', numeric: true, disablePadding: false, label: 'Ações' },
  ];

  const createData = (props:any) => {
    const { id, sigemp, nomfor, datemi, ItemEmissaOrdemCompra} = props

    let valortotal = 0;
    ItemEmissaOrdemCompra.forEach((item:any) => {
      valortotal += item.valortotal
    })

    return { id, sigemp, nomfor, valortotal, datemi, ...props };
  }

  const handleCloseDrawer = () => {
    setSelected([])
    setFields(fieldsInit);
    setSelectedFieldsItems([]);
    setIsDrawerOpen({open: false, btnTitle: 'Salvar', btnType: 'save'});
  };

  const handleCloseDrawerItem = () => {
    setSelected([])
    setSelectedFieldsItems([]);
    setIsDrawerItemOpen(false);
  };

  const handleCloseAddDrawer = () => {
    setSelected([])
    setFields(fieldsInit);
    setIsDrawerOpen({open: false, btnTitle: 'Salvar', btnType: 'save'});
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setIsSnackbar({open: false, message: '', title: 'info'});
  };

  const handleCloseErro = () => {
    setIsDialogFeedbackOpen({ open:false, message: '', title: '', btnType: 'erro'});
    setSelected([])
    setFields(fieldsInit);
    setSelectedFieldsItems([]);
    setReasonCancel('');
  }

  const handleBackErro = () => {
    setIsDialogFeedbackOpen({ open:false, message: '', title: '', btnType: 'erro'});
    setReasonCancel('');
  }

  const { isFetching, refetch } = useQuery(['purchaseOrderEmit'], async () => {
     // obtem as requisicao rascunho
     const token = localStorage.getItem('token')
     if(token){
       const res = await getDraftEmitPurchaseOrderByUser(token, {
         take: rowsPerPage,
         skip: page*rowsPerPage,
       })
       if(res){
         setSelectedData(res.results)
         setCount(res.total)
       }
     }
     return []
   }, {
     refetchOnWindowFocus: false,
   })
  
  useEffect(() => {
    refetch();
  }, [page, refetch]);

  const Alert = (props: AlertProps) => {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
  }

  const handleEmptyField = (val: any, type: string) => {
    if(val){
      return type === 'string' ? val+'' : val;
    } else {
      return null;
    } 
  }

  const handleFormatedDataToDatabase = async (btnType: string) => {
    setIsLoading(true)

    const formData = new FormData();
    const company = fields.filter((field) => field.id === 'dados')[0].value;
    const filial = fields.filter((field) => field.id === 'filiais')[0].value;
    const transacao = fields.filter((field) => field.id === 'transacao')[0].value;
    const condPgto = fields.filter((field) => field.id === 'condPgto')[0].value;
    const fornecedor = fields.filter((field) => field.id === 'fornecedor')[0].value;
    const datemi = fields.filter((field) => field.id === 'datemi')[0].value;
    const obs = fields.filter((field) => field.id === 'obs')[0].value;

    formData.append('codemp', handleEmptyField(company?.codemp, 'number'))
    formData.append('nomemp', handleEmptyField(company?.nomemp, 'string'))
    formData.append('sigemp', handleEmptyField(company?.sigemp, 'string'))
    formData.append('codfil', handleEmptyField(filial?.codfil, 'number'))
    formData.append('nomfil', handleEmptyField(filial?.nomfil, 'string'))
    formData.append('codtns', handleEmptyField(transacao?.codtns, 'number'))
    formData.append('destns', handleEmptyField(transacao?.destns, 'string' ))
    formData.append('codcpg', handleEmptyField(condPgto?.codcpg, 'number' ))
    formData.append('descpg', handleEmptyField(condPgto?.descpg, 'string' ))
    formData.append('codfor', handleEmptyField(fornecedor?.codfor, 'number' ))
    formData.append('nomfor', handleEmptyField(fornecedor?.nomfor, 'string' ))
    if (
      condPgto?.cgccpf !== 'null' && 
      condPgto?.cgccpf !== null &&
      condPgto?.cgccpf !== undefined &&
      condPgto?.cgccpf !== ''
    )
    formData.append('cgccpf', handleEmptyField(fornecedor?.cgccpf, 'string' ))
    formData.append('datemi', handleEmptyField(datemi?.datemi, 'string' ))
    formData.append('obs', handleEmptyField(obs, 'string' ))

    const itens = fields.filter((field) => field.id === 'itens')[0].value.map((item:any, index: number) => {
      const produto = item.filter((field:any) => field.id === 'produto')[0].value;
      const servico = item.filter((field:any) => field.id === 'servico')[0].value;
      const derivacao = item.filter((field:any) => field.id === 'derivacao')[0].value;
      const tipo = item.filter((field:any) => field.id === 'tipo')[0].value;
      const datprv = item.filter((field:any) => field.id === 'datprv')[0].value;
      const unidademedida = item.filter((field:any) => field.id === 'unidademedida')[0].value;
      const qtd = item.filter((field:any) => field.id === 'qtd')[0].value;
      const valorunitario = item.filter((field:any) => field.id === 'valorunitario')[0].value;
  
      let obj = {} as any;
      obj['tipo'] = handleEmptyField(tipo?.tipo, 'string');
      
      if (tipo?.tipo === 'produto') {
        obj['coditem'] = handleEmptyField(produto?.codpro, 'number');
        obj['desitem'] = handleEmptyField(produto?.despro, 'string');
      } else {
        obj['coditem'] = handleEmptyField(servico?.codser, 'number');
        obj['desitem'] = handleEmptyField(servico?.desser, 'string');
      }
      if(derivacao){
        obj['codder'] = handleEmptyField(derivacao?.codder, 'number');
        obj['desder'] = handleEmptyField(derivacao?.desder, 'string');
      }
      obj['codccu'] = item.filter((field:any) => field.id === 'centrosCusto')[0].value?.codccu;
      obj['desccu'] = item.filter((field:any) => field.id === 'centrosCusto')[0].value?.desccu;
      obj['codFinanceira'] = `${item.filter((field:any) => field.id === 'ctaFinanceira')[0].value?.codFinanceira}`;
      obj['desFinanceira'] = item.filter((field:any) => field.id === 'ctaFinanceira')[0].value?.desFinanceira;
      obj['codContabil'] = `${item.filter((field:any) => field.id === 'ctaContabil')[0].value?.codContabil}`;
      obj['desContabil'] = item.filter((field:any) => field.id === 'ctaContabil')[0].value?.desContabil;

      obj['datprv'] = handleEmptyField(datprv?.datprv, 'string');
      obj['unidademedida'] = handleEmptyField(unidademedida, 'string');
      const qtdItem =  handleEmptyField(qtd?.qtd, 'number');
      const valorunitarioItem = handleEmptyField(valorunitario?.valorunitario, 'number');
      obj['qtd'] = qtdItem;
      obj['valorunitario'] = valorunitarioItem;
      obj['valortotal'] = qtdItem * valorunitarioItem;
      return obj;
    })
    formData.append(`itens`, JSON.stringify(itens));
    const parcelas = fields.filter((field) => field.id === 'parcelas')[0].value.map((item:any) => {
      const vctpar = item.filter((field:any) => field.id === 'vctpar')[0].value;
      const seqpar = item.filter((field:any) => field.id === 'seqpar')[0].value;
      const vlrpar = item.filter((field:any) => field.id === 'vlrpar')[0].value;
  
      let obj = {} as any;
      obj['vctpar'] = handleEmptyField(vctpar?.vctpar, 'string');
      obj['seqpar'] = handleEmptyField(seqpar?.seqpar, 'number');
      obj['vlrpar'] = handleEmptyField(vlrpar?.vlrpar, 'number');
      return obj;
    })
    formData.append(`parcelas`, JSON.stringify(parcelas));
    const arquivos = fields.filter((field) => field.id === 'arquivos')[0].value;
    for (const file of arquivos?.arquivos) {
      formData.append('arquivos', file);
    }

    const useCaseSuccess = {
      edit: 'editar ordem compra',
      save: 'criar ordem compra',
      remove: 'remover ordem compra',
      proccess: `processar ordem compra`
    } as any
    const token = localStorage.getItem('token')
    let res = {
      statusCode: 400,
    } as any

    if(token){
      if(btnType === 'save')
        res = await createEmitPurchaseOrderDraft(formData, token)
      if(btnType === 'edit' && selected.length > 0)
        res = await updateEmitPurchaseOrder(selected[0], formData, token)
      if(btnType === 'remove' && selected.length > 0)
        res = await deleteEmitPurchaseOrder(selected[0], token)
      if(btnType === 'proccess' && selected.length > 0)
        res = await processEmitPurchaseOrder(selected, token)
       
      setIsLoading(false)
      if(res.statusCode === 200){
        setIsSnackbar({
          open: true,
          message: `Sucesso ao ${useCaseSuccess[btnType]}`,
          title: 'success'
        })
        setIsDialogFeedbackOpen({
          open: false,
          message: '',
          title: '',
          btnType,
        })
        setSelected([])
      } else {
        if(selected.length === 1) {
          const errorMessage = res.message || selectedData.filter((data:any) => data.id === selected[0])[0].msgerro
          setIsDialogFeedbackOpen({
            open: true,
            message: errorMessage,
            title: 'Erro Erp',
            btnType: 'erro'
          })
        }
        setIsSnackbar({
          open: true,
          message: `Erro ao ${useCaseSuccess[btnType]}`,
          title: 'error'
        })
      }
      refetch()
      setIsDrawerOpen({
        open: false,
        btnTitle: '',
        btnType: '',
      })
      setFields(fieldsInit)
      
    } else {
      handleBackErro();
      setIsSnackbar({
        open: true,
        message: `Erro ao ${useCaseSuccess[btnType]}`,
        title: 'error'
      })
    }
  } 

  const handleOpenDrawer = () => {
    setIsDrawerOpen({open: true, btnTitle: 'Salvar', btnType: 'save'});
  };

  const handleCheckFields = () => {
    const requireds = fields.filter((field) => field.required);

    const hasValue = (value: any): boolean => {
      if (value === null || value === undefined) return false;

      if (typeof value === 'string') return value.trim() !== '';

      if (Array.isArray(value)) return value.length > 0;

      if (typeof value === 'object') {
        return Object.values(value).some((v) => {
          if (v === null || v === undefined) return false;
          if (typeof v === 'string') return v.trim() !== '';
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === 'object') return hasValue(v);
          return true;
        });
      }

      return true;
    };

    const hasAllRequired = requireds.every((required) => hasValue(required.value));

    if (!hasAllRequired) {
      setIsSnackbar({ open: true, message: 'Por favor, adicione os campos obrigatórios!', title: 'error' });
    } else {
      handleDialogConfirmation(isDrawerOpen.btnType)
    }
  };

  const handleOpenDialogSearch = (data: DialogSearchData) => {
    setIsDialogSearchOpen(true);
    setDialogDataSearch(data);
  };

  const handleUpdateField = (data: any) => {
    const update = [...fields];
    update[update.findIndex((field) => field.id === dialogDataSearch?.id)].value = 
      data[dialogDataSearch?.key || ''] === 'Nenhum item' ? null : data
    setFields(update)
    setIsDialogSearchOpen(false);
    setDialogDataSearch(null);
    if(dialogDataSearch?.id === 'dados')
      setCheckPrevCompanyValue(handleCheckPrevValue('dados', update, 'codemp'))
    if(dialogDataSearch?.id === 'filiais')
      setCheckPrevBranchValue(handleCheckPrevValue('filiais', update, 'codfil'))
  }

  const handleInfo = (id:any) => {
    const item = selectedData.filter((data:any) => data.id === id )
    if(item.length > 0){
      setFields(mapperToPurchaseOrder(item[0]));
      setSelectedFieldsItems(item[0].ItemEmissaOrdemCompra)
      setIsDrawerItemOpen(true)
    }
  }

  const handleApproved = (id:any) => {
    handleDialogConfirmation('proccess') 
  }

  const handleRemove = (id:any) => {
    handleDialogConfirmation('remove') 
  }

  const handleEdit = (id:string) => {
    const item = selectedData.filter((data:any) => data.id === id )
    const updated = mapperToPurchaseOrder(item[0])

    setFields(updated)
    setCheckPrevCompanyValue(handleCheckPrevValue('dados', updated, 'codemp'))
    setCheckPrevBranchValue(handleCheckPrevValue('filiais', updated, 'codfil'))
    setIsDrawerOpen({open: true, btnTitle: 'Editar', btnType: 'edit'});
  }

  const handleCloseDialogSearch = () => {
    setIsDialogSearchOpen(false);
    setDialogDataSearch(null);
    setSelected([]);
  };

  const handleDialogConfirmation = (type: string) => {
    const useCaseFeedbackTitle = {
      save: 'Deseja salvar ordem de compra',
      edit: 'Deseja editar ordem de compra',
      remove: 'Deseja remover ordem de compra',
      proccess: `Deseja gerar ordem de compra`,
    } as any

    if (type === 'save') {
      handleFormatedDataToDatabase('save')
    } else {
      setIsDialogFeedbackOpen({
        open: true,
        title: useCaseFeedbackTitle[type],
        message: '',
        btnType: type,
      })
    }
  }

  const handleCheckError = () => {
    const itemSelected = selectedData.filter((data:any) => data.id === selected[0])
    if(itemSelected){
      const erro =  Object.entries(itemSelected[0]).find(([key, val]) => key.includes('msgerro'))
      if(erro && erro[1] !== null && erro[1] !== '')
        return true
      else
        return false
    }
    return false
  }

  const handleErro = (id:string) => {
    setIsDialogFeedbackOpen({
      open: true,
      message: selectedData.filter((data:any) => data.id === id)[0].msgerro,
      title: 'Erro Erp',
      btnType: 'erro'
    })
  }
  
  return (
    <div className={classes.root}>
      <PurchaseOrderEmitHeader
        onClick={handleOpenDrawer}
      />
      <div className={classes.tableSection}>
        <Container className={classes.tableSectionContainer}>
          <EnhancedTable 
            title=''
            rowsPerPageOptions={[]}
            isClickable
            data={selectedData?.map((data:any) => createData(data) )}
            count={count}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            headCells={headCells}
            isLoading={isFetching}
            selected={selected}
            setSelected={setSelected}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            buttonsSelected={[ 
              selected.length === 1 && 
                <Tooltip title="Processar" onClick={handleApproved} key='proccess'>
                  <IconButton aria-label="proccess">
                    <DoneIcon style={{color: customtheme.white}}/>
                  </IconButton>
                </Tooltip>,
              selected.length === 1 && 
                <Tooltip
                  title="Editar" 
                  onClick={() => handleEdit(`${selected[0]}`)}
                  key='edit'
                >
                  <IconButton aria-label="edit">
                    <EditIcon style={{color: customtheme.white}}/>
                  </IconButton>
                </Tooltip>,
              selected.length === 1 && 
                <Tooltip
                  title="Info"
                  onClick={() => handleInfo(`${selected[0]}`)}
                  key='info'
                >
                <IconButton aria-label="info">
                  <ErrorIcon style={{color: customtheme.white}}/>
                </IconButton>
              </Tooltip>,
              selected.length === 1 && 
                <Tooltip title="Remover" onClick={handleRemove} key='remove'>
                  <IconButton aria-label="remove">
                    <CloseIcon style={{color: customtheme.white}}/>
                  </IconButton>
                </Tooltip>,
                (selected.length === 1 && handleCheckError()) && 
              <Tooltip
                title="Erro"
                onClick={() => handleErro(`${selected[0]}`)}
                key='erro'
              >
              <IconButton aria-label="erro">
                <ErrorIcon style={{color: customtheme.white}}/>
              </IconButton>
            </Tooltip>,
            ]}
            buttonsActions={['edit', 'itens', 'remove', 'erro']}
            handleInfo={handleInfo}
            handleEdit={handleEdit}
            handleRemove={handleRemove}
            handleErro={handleErro}
          />
        </Container>
      </div>
      <Drawer
        anchor="right"
        open={isDrawerItemOpen}
        onClose={handleCloseDrawerItem}
        classes={{ paper: classes.drawer }}
      >
        <div className={classes.drawerHeader}>
          <Typography variant="h6">Itens</Typography>
        </div>
        <div className={classes.drawerContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                className={classes.tabsBar}
              >
                <Tab label="Produtos"classes={{ root: classes.tabRoot }} />
                <Tab label="Serviços"classes={{ root: classes.tabRoot }} />
                <Tab label="Parcelas" classes={{ root: classes.tabRoot }}/>
                <Tab label="Anexos" classes={{ root: classes.tabRoot }}/>
              </Tabs>

              <TabPanel value={tab} index={0}>
                {
                  selectedFieldsItems.filter(((item) => item.tipo === 'produto')).length === 0 && 
                  <Box mt={3}>
                    <Typography>Nenhum produto criado</Typography>
                  </Box>
                }
                <ItemsDrawer
                  fields={selectedFieldsItems.filter(((item) => item.tipo === 'produto'))} 
                  fieldsItems={fieldsItems}
                  keyTitle='descricao'
                  keySubTitle=''
                  labelsFields={
                    [
                      {
                        key: 'coditem',
                        value: 'Cód item',
                      },
                      {
                        key: 'desitem',
                        value: 'Descrição item',
                      },
                      {
                        key: 'codder',
                        value: 'Código derivação',
                      },
                      {
                        key: 'desder',
                        value: 'Descrição derivação',
                      },
                      {
                        key: 'qtd',
                        value: 'Quantidade',
                      },
                      {
                        key: 'datprv',
                        value: 'Data entrega',
                      },
                      {
                        key: 'valortotal',
                        value: 'Valor Total',
                      },
                      {
                        key: 'valorunitario',
                        value: 'Valor Unitário',
                      },
                      {
                        key: 'unidademedida',
                        value: 'Unidade de Medida',
                      },
                      {
                        key: 'codFinanceira',
                        value: 'Código Conta Financeira',
                      },
                      {
                        key: 'desFinanceira',
                        value: 'Descrição Conta Financeira',
                      },
                      {
                        key: 'codContabil',
                        value: 'Código Conta Contabil',
                      },
                      {
                        key: 'desContabil',
                        value: 'Descrição Conta Contabil',
                      },
                      {
                        key: 'codccu',
                        value: 'Centro de custos',
                      },
                      {
                        key: 'desccu',
                        value: 'Descrição centro de custos',
                      },
                    ]
                  }
                />
              </TabPanel>

              <TabPanel value={tab} index={1}>
                {
                  selectedFieldsItems.filter(((item) => item.tipo === 'servico')).length === 0 && 
                  <Box mt={3}>
                    <Typography >Nenhum serviço criado</Typography>
                  </Box>
                }
                <ItemsDrawer
                  fields={selectedFieldsItems.filter(((item) => item.tipo === 'servico'))} 
                  fieldsItems={fieldsItems}
                  keyTitle='descricao'
                  keySubTitle=''
                  labelsFields={
                    [
                      {
                        key: 'coditem',
                        value: 'Cód item',
                      },
                      {
                        key: 'desitem',
                        value: 'Descrição item',
                      },
                      {
                        key: 'codder',
                        value: 'Código derivação',
                      },
                      {
                        key: 'desder',
                        value: 'Descrição derivação',
                      },
                      {
                        key: 'qtd',
                        value: 'Quantidade',
                      },
                      {
                        key: 'datprv',
                        value: 'Data entrega',
                      },
                      {
                        key: 'valortotal',
                        value: 'Valor Total',
                      },
                      {
                        key: 'valorunitario',
                        value: 'Valor Unitário',
                      },
                      {
                        key: 'unidademedida',
                        value: 'Unidade de Medida',
                      },
                      {
                        key: 'codFinanceira',
                        value: 'Código Conta Financeira',
                      },
                      {
                        key: 'desFinanceira',
                        value: 'Descrição Conta Financeira',
                      },
                      {
                        key: 'codContabil',
                        value: 'Código Conta Contabil',
                      },
                      {
                        key: 'desContabil',
                        value: 'Descrição Conta Contabil',
                      },
                      {
                        key: 'codccu',
                        value: 'Centro de custos',
                      },
                      {
                        key: 'desccu',
                        value: 'Descrição centro de custos',
                      },
                    ]
                  }
                />
              </TabPanel>

              <TabPanel value={tab} index={1}>
                {
                  selectedFieldsItems.filter(((item) => item.tipo === 'servico')).length === 0 && 
                  <Box mt={3}>
                    <Typography >Pagamento a vista</Typography>
                  </Box>
                }
                <ItemsDrawer
                  fields={selectedFieldsItems.filter(((item) => item.tipo === 'servico'))} 
                  fieldsItems={fieldsItems}
                  keyTitle='descricao'
                  keySubTitle=''
                  labelsFields={
                    [
                      {
                        key: 'coditem',
                        value: 'Cód item',
                      },
                      {
                        key: 'desitem',
                        value: 'Descrição item',
                      },
                      {
                        key: 'codder',
                        value: 'Código derivação',
                      },
                      {
                        key: 'desder',
                        value: 'Descrição derivação',
                      },
                      {
                        key: 'qtd',
                        value: 'Quantidade',
                      },
                      {
                        key: 'datprv',
                        value: 'Data entrega',
                      },
                      {
                        key: 'valortotal',
                        value: 'Valor Total',
                      },
                      {
                        key: 'valorunitario',
                        value: 'Valor Unitário',
                      },
                      {
                        key: 'unidademedida',
                        value: 'Unidade de Medida',
                      },
                      {
                        key: 'codFinanceira',
                        value: 'Código Conta Financeira',
                      },
                      {
                        key: 'desFinanceira',
                        value: 'Descrição Conta Financeira',
                      },
                      {
                        key: 'codContabil',
                        value: 'Código Conta Contabil',
                      },
                      {
                        key: 'desContabil',
                        value: 'Descrição Conta Contabil',
                      },
                      {
                        key: 'codccu',
                        value: 'Centro de custos',
                      },
                      {
                        key: 'desccu',
                        value: 'Descrição centro de custos',
                      },
                    ]
                  }
                />
              </TabPanel>
              <TabPanel value={tab} index={3}>
                <Grid container spacing={2} style={{ padding: 8, marginTop: 16, gap: 16 }}>
                  <Box mt={2} width="100%">
                    {(fields.find(f => f.id === 'arquivos')?.value?.arquivos || []).map((file: File, idx: number) => (
                      <Grid
                        key={idx}
                        container
                        alignItems="center"
                        spacing={1}
                        style={{ marginBottom: 8 }}
                      >
                        {/* Link clicável para visualizar */}
                        <Grid item xs>
                          <Typography
                            variant="body2"
                            component="span"
                            style={{ textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={() => {
                              const url = URL.createObjectURL(file);
                              window.open(url, '_blank');
                              setTimeout(() => URL.revokeObjectURL(url), 1000);
                            }}
                          >
                            {file.name}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))}
                    {(fields.find(f => f.id === 'arquivos')?.value?.arquivos || []).length === 0 && 
                      <Typography>
                        Nenhum arquivo inserido
                      </Typography>
                    }
                  </Box>
                </Grid>
              </TabPanel>
            </Grid>
          </Grid>
        </div>
        <div className={classes.drawerFooter}>
          <Button
            onClick={handleCloseDrawer}
            className={classes.backBackgroundColor}
          >
            Voltar
          </Button>
        </div>
      </Drawer>
      <PurchaseOrderEmitDialog
        handleCloseErro={handleCloseErro}
        handleBackErro={handleBackErro}
        handleFormatedDataToDatabase={handleFormatedDataToDatabase}
        isDialogFeedbackOpen={isDialogFeedbackOpen}
        isLoading={isLoading}
        reasonCancel={reasonCancel}
        setReasonCancel={setReasonCancel}
      />
      <PurchaseOrderEmitDrawer
        checkPrevCompanyValue={checkPrevCompanyValue}
        checkPrevBranchValue={checkPrevBranchValue}
        fields={fields}
        setFields={setFields}
        setIsSnackbar={setIsSnackbar}
        handleCheckFields={handleCheckFields}
        handleCloseDrawer={handleCloseAddDrawer}
        handleCloseSnackbar={handleCloseSnackbar}
        handleOpenDialogSearch={handleOpenDialogSearch}
        isDrawerOpen={isDrawerOpen}
      />
      <DialogSearch
        codEmp={handleCheckPrevValue('dados', fields, 'codemp').value || ''}
        codFil={handleCheckPrevValue('filiais', fields, 'codfil').value || ''}
        title={dialogDataSearch?.title || ''}
        id={dialogDataSearch?.id || ''}
        keyItem={dialogDataSearch?.key || ''}
        keySubItem={dialogDataSearch?.keySub || ''}
        subKeySubItem={dialogDataSearch?.subKeySub}
        isDialogOpen={isDialogSearchOpen}
        isPrevSearch={dialogDataSearch?.isPrevSearch || false}
        prevSearch={dialogDataSearch?.prevSearch || []}
        prevDataSearch={dialogDataSearch?.prevDataSearch}
        handleCloseDialog={handleCloseDialogSearch}
        handleUpdateField={(data:any) => { handleUpdateField(data) }}
        hasButtonCreate={dialogDataSearch?.hasButtonCreate || false}
      />
      <Snackbar open={isSnackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={isSnackbar.title as any}>
          {isSnackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PurchaseOrderEmit;